import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { Upload, CheckCircle2 } from "lucide-react";

const BrandOnboarding = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({
        company_name: "", sector: "", website: "",
        audience_description: "", brand_guidelines_text: "", goals: "",
    });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/brand/profile").then((r) => {
            setProfile(r.data);
            setForm({
                company_name: r.data.company_name || "",
                sector: r.data.sector || "",
                website: r.data.website || "",
                audience_description: r.data.audience_description || "",
                brand_guidelines_text: r.data.brand_guidelines_text || "",
                goals: r.data.goals || "",
            });
        });
    }, []);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post("/brand/upload-guidelines", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const r = await api.get("/brand/profile");
            setProfile(r.data);
        } finally { setUploading(false); }
    };

    const finish = async () => {
        setSaving(true);
        try {
            await api.post("/brand/onboarding", form);
            navigate("/brand/membership");
        } finally { setSaving(false); }
    };

    return (
        <DashboardShell role="brand" title="Brand onboarding" subtitle={`Step ${step} of 3`}>
            <div className="max-w-3xl mx-auto w-full">
                <div className="flex items-center gap-2 mb-8">
                    {[1, 2, 3].map((n) => (
                        <div key={n} className={`flex-1 h-3 rounded-full border-2 border-yc-ink ${n <= step ? "bg-yc-blue" : "bg-white"}`} />
                    ))}
                </div>

                {step === 1 && (
                    <div className="yc-card p-8 space-y-5">
                        <h2 className="font-heading text-2xl font-bold">Tell us about your company</h2>
                        <div>
                            <label className="yc-overline">Company name</label>
                            <input data-testid="ob-company" className="yc-input mt-2" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="yc-overline">Sector</label>
                                <input data-testid="ob-sector" className="yc-input mt-2" placeholder="Fashion, SaaS…" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
                            </div>
                            <div>
                                <label className="yc-overline">Website</label>
                                <input data-testid="ob-website" className="yc-input mt-2" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                            </div>
                        </div>
                        <button data-testid="ob-next-1" className="yc-btn-primary" onClick={() => setStep(2)}>Continue</button>
                    </div>
                )}

                {step === 2 && (
                    <div className="yc-card p-8 space-y-5">
                        <h2 className="font-heading text-2xl font-bold">Audience & brand voice</h2>
                        <div>
                            <label className="yc-overline">Target audience</label>
                            <textarea data-testid="ob-audience" rows={4} className="yc-input mt-2" value={form.audience_description} onChange={(e) => setForm({ ...form, audience_description: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Brand guidelines (paste key notes)</label>
                            <textarea data-testid="ob-guidelines-text" rows={4} className="yc-input mt-2" value={form.brand_guidelines_text} onChange={(e) => setForm({ ...form, brand_guidelines_text: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Upload guideline files (optional)</label>
                            <label className="mt-2 flex items-center gap-3 yc-btn-secondary cursor-pointer" data-testid="ob-upload-label">
                                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose file"}
                                <input data-testid="ob-upload-file" type="file" hidden onChange={upload} />
                            </label>
                            {profile?.brand_guidelines_files?.length > 0 && (
                                <ul className="mt-3 space-y-1 text-sm">
                                    {profile.brand_guidelines_files.map((f, i) => (
                                        <li key={f.stored_path || f.original_filename || `bg-${i}`} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-yc-blue" /> {f.original_filename}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button className="yc-btn-secondary" onClick={() => setStep(1)}>Back</button>
                            <button data-testid="ob-next-2" className="yc-btn-primary" onClick={() => setStep(3)}>Continue</button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="yc-card p-8 space-y-5">
                        <h2 className="font-heading text-2xl font-bold">What are your goals?</h2>
                        <textarea data-testid="ob-goals" rows={6} className="yc-input mt-2" placeholder="Awareness, sales lift, content library, hiring brand creators…" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} />
                        <div className="flex gap-3">
                            <button className="yc-btn-secondary" onClick={() => setStep(2)}>Back</button>
                            <button data-testid="ob-finish" disabled={saving} className="yc-btn-primary" onClick={finish}>{saving ? "Saving…" : "Finish & continue to membership"}</button>
                        </div>
                    </div>
                )}
            </div>
        </DashboardShell>
    );
};

export default BrandOnboarding;
